/**
 * Format a byte count into a human-readable string (e.g., "142.3 MB").
 * Returns '—' for zero bytes by default; pass zeroLabel to customize.
 */
export function formatBytes(bytes: number, zeroLabel = '—'): string {
  if (bytes === 0) return zeroLabel;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Check whether a tier/quota limit represents "unlimited".
 * The backend signals unlimited with -1 across all tier limits
 * (maxUsers, maxWorkflows, maxHosts, maxOrganizations, storageGb, limitBytes, etc.).
 */
export function isUnlimited(value: number): boolean {
  return value < 0;
}

/**
 * Format a tier limit for display. Returns "Unlimited" for -1 values,
 * otherwise returns the number with an optional suffix.
 *
 * @example
 * formatTierLimit(tier.maxWorkflows)           // "Unlimited" or "50"
 * formatTierLimit(tier.storageGb, 'GB')        // "Unlimited" or "100GB"
 * formatTierLimit(tier.maxUsers, ' users')     // "Unlimited" or "10 users"
 */
export function formatTierLimit(value: number, suffix = ''): string {
  if (isUnlimited(value)) return 'Unlimited';
  return `${value}${suffix}`;
}

/**
 * Format a storage limit in bytes as a human-readable string.
 * Returns "Unlimited" for negative values (unlimited quota), otherwise formats bytes.
 */
export function formatStorageLimit(limitBytes: number): string {
  if (isUnlimited(limitBytes)) return 'Unlimited';
  return formatBytes(limitBytes, '0 B');
}

/**
 * Capitalize the first character of a string.
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Humanize a backend enum/status string for display so raw DB values never
 * render verbatim. e.g. "past_due" -> "Past due", "active" -> "Active",
 * "incomplete_expired" -> "Incomplete expired".
 */
export function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  return capitalize(status.replace(/_/g, ' '));
}

/**
 * Format an integer cent amount as a dollar string with a `$` adornment and
 * locale-aware thousands separators. Use everywhere money is shown so cents
 * never leak to the UI as raw integers.
 *
 * @example
 * formatCents(123456)      // "$1,234.56"
 * formatCents(4900)        // "$49.00"
 */
export function formatCents(cents: number, fractionDigits = 2): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

/**
 * Shorten an opaque id (UUID) for display as a muted secondary/copy affordance,
 * keeping the leading segment. Raw UUIDs should never be a row's primary label.
 *
 * @example
 * shortId('a1b2c3d4-e5f6-7890-abcd-ef1234567890') // "a1b2c3d4"
 */
export function shortId(id: string | null | undefined, length = 8): string {
  if (!id) return '';
  return id.length <= length ? id : id.slice(0, length);
}

/**
 * Format a date string as a relative time (e.g., "3d ago", "2h ago").
 * Returns 'Never' for null values.
 */
export function formatRelativeTime(dateStr: string | null): string {
  if (dateStr === null) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

/**
 * Number of days before an agreement's end date at which the "expiring soon"
 * advisory begins. The end date is advisory only — there is no runtime
 * enforcement or auto-suspend tied to it.
 */
export const AGREEMENT_EXPIRY_WINDOW_DAYS = 30;

/**
 * Advisory state for a partner agreement's end date. Purely visual.
 *
 * - `'expired'`   the end date is in the past.
 * - `'expiring'`  the end date falls within the next 30 days (and is not past).
 * - `null`        no end date, an unparseable date, or more than 30 days out.
 *
 * Pass an explicit `now` for deterministic tests; defaults to the current time.
 */
export function agreementEndAdvisory(
  endDate: string | null,
  now: Date = new Date(),
): 'expired' | 'expiring' | null {
  if (!endDate) return null;
  const end = new Date(endDate).getTime();
  if (Number.isNaN(end)) return null;
  const diffMs = end - now.getTime();
  if (diffMs < 0) return 'expired';
  const windowMs = AGREEMENT_EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  if (diffMs <= windowMs) return 'expiring';
  return null;
}

/**
 * Human duration between two ISO timestamps, e.g. "6s", "6m 22s", "1h 5m".
 * Returns an em dash when either bound is missing (run not started/finished).
 */
export function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (startedAt === null || completedAt === null) return '—';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m ${totalSeconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
